import { PayloadAction } from '@reduxjs/toolkit'
import { reducerCreatorProducer } from 'store/common'
import {
  IAIVideoDefaultSettings,
  IAIVideoState,
  IVersionHistoryItem
} from 'store/types/aiVideo'
import {
  IAIVideoModel,
  IAIVideoGenerationParams,
  IAIVideoProject,
  IReferenceMediaItem,
  TReferenceMode,
  ReferenceMediaImageType
} from 'types/aiVideo'
import {
  getDefaultAIVideoModelGenerationParams,
  getDefaultImageAspectRatio,
  getDefaultReferenceMode,
  getModelDefaultOptionCredits,
  swapFramesBucket
} from './utils'
import { ReferenceMode } from 'containers/SubSidebar/AIVideoContent/consts'

const createBucketIfNotExists = (
  state: IAIVideoState,
  bucket: TReferenceMode
) => {
  if (!state.generationParams.referenceMedias) {
    state.generationParams.referenceMedias = {}
  }
  if (!state.generationParams.referenceMedias[bucket]) {
    state.generationParams.referenceMedias[bucket] = []
  }
}

const _applyDefaultImageAspectRatio = (state: IAIVideoState) => {
  const next = getDefaultImageAspectRatio(state)
  if (next === undefined) return
  state.generationParams.aspectRatio = next
}

const reducers = reducerCreatorProducer<IAIVideoState>()({
  // Generating reducers
  setIsRequestingGeneration: (state, action: PayloadAction<boolean>) => {
    state.isRequestingGeneration = action.payload
  },

  // Reference mode
  setReferenceMode: (state, action: PayloadAction<TReferenceMode>) => {
    state.referenceMode = action.payload
  },

  // Reference media item reducers (bucket-based)
  setReferenceMediaItemLoading: (
    state,
    action: PayloadAction<{
      id: string
      isLoading: boolean
      bucket: TReferenceMode
    }>
  ) => {
    const { id, isLoading, bucket } = action.payload
    const items = state.generationParams.referenceMedias?.[bucket]
    const item = items?.find(item => item.id === id)
    if (item) {
      item.isLoading = isLoading
    }
  },

  addReferenceMediaItem: (
    state,
    action: PayloadAction<{ item: IReferenceMediaItem; bucket: TReferenceMode }>
  ) => {
    const { item, bucket } = action.payload
    createBucketIfNotExists(state, bucket)
    if (bucket === ReferenceMode.IMAGES) {
      state.generationParams.referenceMedias![bucket]!.push(item)
    }

    if (bucket === ReferenceMode.FRAMES) {
      const framesBucket = state.generationParams.referenceMedias![bucket]!
      const newFramesBucket = framesBucket.filter(
        frame => frame.type !== item.type
      )
      newFramesBucket.push(item)
      state.generationParams.referenceMedias![bucket] = newFramesBucket
      _applyDefaultImageAspectRatio(state)
    }
    state.generationParams.aiImageId = null
  },

  removeReferenceMediaItem: (
    state,
    action: PayloadAction<{ id: string; bucket: TReferenceMode }>
  ) => {
    const { id, bucket } = action.payload
    const items = state.generationParams.referenceMedias?.[bucket]
    if (!items) return
    state.generationParams.referenceMedias![bucket] = items.filter(
      item => item.id !== id
    )
    if (bucket === ReferenceMode.FRAMES) {
      _applyDefaultImageAspectRatio(state)
    }
  },

  updateReferenceMediaItem: (
    state,
    action: PayloadAction<{
      id: string
      updates: Partial<IReferenceMediaItem>
      bucket: TReferenceMode
    }>
  ) => {
    const { id, updates, bucket } = action.payload
    const items = state.generationParams.referenceMedias?.[bucket]
    const item = items?.find(i => i.id === id)
    if (item) {
      Object.assign(item, updates)
    }
  },

  // Bulk parameter updates
  setGenerationParams: (
    state,
    action: PayloadAction<IAIVideoGenerationParams>
  ) => {
    state.generationParams = action.payload
  },

  updateGenerationParams: (
    state: IAIVideoState,
    action: PayloadAction<Partial<IAIVideoGenerationParams>>
  ) => {
    const updatedForm = { ...state.generationParams, ...action.payload }
    state.generationParams = updatedForm
  },

  clearGenerationParams: (
    state,
    action: PayloadAction<IAIVideoModel | null>
  ) => {
    const defaultModel = action.payload ?? undefined
    const defaultParams: IAIVideoGenerationParams =
      getDefaultAIVideoModelGenerationParams(defaultModel)
    state.generationParams = defaultParams
    // TODO: add "noUncheckedIndexedAccess": true, in our tsconfig.json
    // because in cases of [] we have to check if the index is not undefined
    // which can actually cause some errors in other cases where we do have this kind of checks
    // where we have empty arrays
    state.referenceMode = getDefaultReferenceMode(defaultModel)
    state.hasUserSelectedImageAspectRatio = false
    state.videoGenerationCost = defaultModel
      ? getModelDefaultOptionCredits({
          selectedModel: defaultModel,
          selectedQuality: defaultParams.quality
        })
      : 0
  },

  setAiImageId: (state, action: PayloadAction<number | null>) => {
    state.generationParams.aiImageId = action.payload
  },
  setSelectedModel: (state, action: PayloadAction<string>) => {
    state.generationParams.selectedModel = action.payload
  },
  setDuration: (state, action: PayloadAction<number>) => {
    state.generationParams.duration = action.payload
  },
  setAspectRatio: (state, action: PayloadAction<string | null>) => {
    state.generationParams.aspectRatio = action.payload
  },
  setHasUserSelectedImageAspectRatio: (
    state,
    action: PayloadAction<boolean>
  ) => {
    state.hasUserSelectedImageAspectRatio = action.payload
  },
  applyDefaultImageAspectRatio: state => {
    _applyDefaultImageAspectRatio(state)
  },
  setQuality: (state, action: PayloadAction<number>) => {
    state.generationParams.quality = action.payload
  },
  setPrompt: (state, action: PayloadAction<string>) => {
    state.generationParams.prompt = action.payload
  },
  setTargetMediaItemId: (state, action: PayloadAction<string | null>) => {
    state.generationParams.targetMediaItemId = action.payload
  },
  setIsConsistencyEnabled: (state, action: PayloadAction<boolean>) => {
    state.generationParams.isConsistencyEnabled = action.payload
  },
  setSmartPrompt: (state, action: PayloadAction<string>) => {
    state.generationParams.smartPrompt = action.payload
  },
  setIsFastTrack: (state, action: PayloadAction<boolean>) => {
    state.generationParams.isFastTrack = action.payload
  },
  setIsPowerModeRequiredPopupOpen: (state, action: PayloadAction<boolean>) => {
    state.isPowerModeRequiredPopupOpen = action.payload
  },
  resetReferenceImagesAndTargetData: state => {
    state.generationParams.aiImageId = null
    state.generationParams.referenceMedias = undefined
    state.generationParams.targetMediaItemId = null
  },
  // Projects History list reducers
  setAiVideoHistoryLoading: (state, action: PayloadAction<boolean>) => {
    state.aiVideoHistory.isLoading = action.payload
  },
  setAiVideoHistory: (state, action: PayloadAction<IAIVideoProject[]>) => {
    state.aiVideoHistory.videos = action.payload
    state.aiVideoHistory.isDataInitiallyLoaded = true
  },
  resetAiVideoHistoryPagination: state => {
    state.aiVideoHistory.videos = []
    state.aiVideoHistory.totalCount = 0
    state.aiVideoHistory.rawOffset = 0
    state.aiVideoHistory.isDataInitiallyLoaded = false
  },
  addAiVideoToHistory: (state, action: PayloadAction<IAIVideoProject>) => {
    state.aiVideoHistory.videos = [
      action.payload,
      ...state.aiVideoHistory.videos
    ]
  },
  updateAiVideoHistoryItem: (
    state,
    action: PayloadAction<{
      id: number | string
      updates: Partial<IAIVideoProject>
    }>
  ) => {
    const { id, updates } = action.payload
    const itemIndex = state.aiVideoHistory.videos.findIndex(
      video => video.id === id
    )
    if (itemIndex !== -1) {
      state.aiVideoHistory.videos[itemIndex] = {
        ...state.aiVideoHistory.videos[itemIndex],
        ...updates
      }
    }
  },

  removeAiVideoHistoryItem: (
    state,
    action: PayloadAction<Array<number | string>>
  ) => {
    state.aiVideoHistory.videos = state.aiVideoHistory.videos.filter(
      video => !action.payload.includes(video.id)
    )
  },

  setIsLoadingTabData: (state, action: PayloadAction<boolean>) => {
    state.isLoadingTabData = action.payload
  },

  setIsModelsFetched: (state, action: PayloadAction<boolean>) => {
    state.isModelsFetched = action.payload
  },

  setAIVideoModels: (state, action: PayloadAction<IAIVideoModel[]>) => {
    state.aiVideoModels = action.payload
  },

  setVideoGenerationCost: (state, action: PayloadAction<number>) => {
    state.videoGenerationCost = action.payload
  },

  setActiveTab: (state, action: PayloadAction<string>) => {
    state.activeTab = action.payload
  },

  setAiVideoHistoryTotalCount: (state, action: PayloadAction<number>) => {
    state.aiVideoHistory.totalCount = action.payload
  },

  setAiVideoHistoryRawOffset: (state, action: PayloadAction<number>) => {
    state.aiVideoHistory.rawOffset = action.payload
  },
  setIsScopedToCurrentProject: (state, action: PayloadAction<boolean>) => {
    state.aiVideoHistory.isScopedToCurrentProject = action.payload
  },

  setRefImageSelectedStyleId: (
    state,
    action: PayloadAction<number | undefined>
  ) => {
    state.generationParams.refImageSelectedStyleId = action.payload
  },

  setVersionHistoryLoading: (state, action: PayloadAction<boolean>) => {
    state.versionHistory.isLoading = action.payload
  },
  setVersionHistoryItems: (
    state,
    action: PayloadAction<IVersionHistoryItem[]>
  ) => {
    state.versionHistory.items = action.payload
    state.versionHistory.isLoaded = true
  },
  resetVersionHistory: state => {
    state.versionHistory.items = []
    state.versionHistory.isLoading = false
    state.versionHistory.isLoaded = false
  },

  setDefaultGenerationSettings: (
    state,
    action: PayloadAction<IAIVideoDefaultSettings>
  ) => {
    state.defaultGenerationSettings = action.payload
  },
  swapFrames: state => {
    const { referenceMedias } = state.generationParams
    const frames = referenceMedias?.frames
    if (!frames) return
    const swappedFrames = swapFramesBucket(frames)
    state.generationParams.referenceMedias = {
      ...referenceMedias,
      frames: swappedFrames
    }
  },
  removeLastFrame: state => {
    const frames = state.generationParams.referenceMedias?.frames
    if (!frames) return
    const lastFrame = frames.find(
      frame => frame.type === ReferenceMediaImageType.LAST_FRAME
    )
    if (!lastFrame) return
    state.generationParams.referenceMedias = {
      ...state.generationParams.referenceMedias,
      frames: frames.filter(
        frame => frame.type !== ReferenceMediaImageType.LAST_FRAME
      )
    }
    _applyDefaultImageAspectRatio(state)
  },

  setShowVersionHistoryTab: (state, action: PayloadAction<boolean>) => {
    state.showVersionHistoryTab = action.payload
  }
})

export default reducers
